import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import type { Role } from '@igd/shared';

declare module 'vue-router' {
  interface RouteMeta {
    title?: string;
    /** Accessible sans session (connexion). */
    public?: boolean;
    /** Rendu sans la coquille (menu, barre du haut). */
    plein?: boolean;
    /** Au moins un de ces rôles est requis. */
    roles?: Role[];
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { title: 'Connexion', public: true },
  },
  {
    path: '/',
    name: 'accueil',
    component: () => import('@/pages/DashboardPage.vue'),
    meta: { title: 'Tableau de bord' },
  },
  {
    path: '/declarations',
    name: 'declarations',
    component: () => import('@/pages/MesDeclarationsPage.vue'),
    meta: { title: 'Mes déclarations' },
  },
  {
    // Déclarée AVANT `/declarations/:id` : sinon « nouvelle » serait pris pour un id.
    path: '/declarations/nouvelle',
    name: 'declaration-nouvelle',
    component: () => import('@/pages/DeclarationFormPage.vue'),
    meta: { title: 'Nouvelle déclaration' },
  },
  {
    path: '/declarations/:id/modifier',
    name: 'declaration-modifier',
    component: () => import('@/pages/DeclarationFormPage.vue'),
    meta: { title: 'Modifier la déclaration' },
  },
  {
    path: '/declarations/:id',
    name: 'declaration',
    component: () => import('@/pages/DeclarationDetailPage.vue'),
    meta: { title: 'Déclaration' },
  },
  {
    // La visite guidée et l'entrée du mode démo : ouvertes à tout utilisateur connecté.
    path: '/decouverte',
    name: 'decouverte',
    component: () => import('@/pages/DecouvertePage.vue'),
    meta: { title: 'Découverte' },
  },
  {
    path: '/validation',
    name: 'validation',
    component: () => import('@/pages/ValidationPage.vue'),
    // L'admin tient le premier niveau en secours (collaborateur sans manager).
    meta: { title: 'À valider', roles: ['manager', 'rh', 'admin'] },
  },
  {
    path: '/paie',
    name: 'paie',
    component: () => import('@/pages/PaiePage.vue'),
    meta: { title: 'Export paie & reporting', roles: ['rh'] },
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/pages/AdminPage.vue'),
    meta: { title: 'Administration', roles: ['admin'] },
  },
  {
    path: '/403',
    name: 'interdit',
    component: () => import('@/pages/ErreurPage.vue'),
    props: { code: 403 },
    meta: { title: 'Accès refusé', plein: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'introuvable',
    component: () => import('@/pages/ErreurPage.vue'),
    props: { code: 404 },
    meta: { title: 'Page introuvable', plein: true },
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

router.beforeEach(async (to) => {
  const { useAuthStore } = await import('@/stores/auth');
  const auth = useAuthStore();
  if (!auth.ready) await auth.init();

  if (to.meta.public) {
    // Déjà connecté : pas la peine de rester sur /login.
    if (to.name === 'login' && auth.isAuthenticated) {
      const redirect = typeof to.query.redirect === 'string' ? to.query.redirect : '/';
      return redirect.startsWith('/') ? redirect : '/';
    }
    return true;
  }
  if (!auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  // L'API refuse de toute façon (403) ; ici on évite d'ouvrir un écran qui ne
  // se remplirait jamais, et l'on dit POURQUOI plutôt que de renvoyer à l'accueil.
  if (to.meta.roles && !auth.hasRole(...to.meta.roles)) {
    return { name: 'interdit' };
  }
  return true;
});

router.afterEach((to) => {
  const title = to.meta.title ?? '';
  document.title = title ? `${title} — Indemnités de déplacement` : 'Indemnités de déplacement — Altonéo';
});
